import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CheckCircle, XCircle, FileText } from 'lucide-react';
import BookingPagination from '../components/BookingPagination';
import { BusyTable } from '../components/TableBusyOverlay';
import usePagedListUi from '../hooks/usePagedListUi';

const PAGE_SIZE = 5;

const statusColors = {
  pending_review: { bg: 'bg-yellow-50', text: 'text-yellow-800', badge: 'status-pending' },
  approved: { bg: 'bg-green-50', text: 'text-green-800', badge: 'status-approved' },
  declined: { bg: 'bg-red-50', text: 'text-red-800', badge: 'status-declined' },
};

export default function AdminQueuePage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminEmail, setAdminEmail] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [actionNote, setActionNote] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const listTopRef = useRef(null);

  const fetchQueue = useCallback(async (email) => {
    setLoading(true);
    try {
      const url = new URL('/api/admin/bookings/', window.location.origin);
      if (statusFilter) url.searchParams.append('status', statusFilter);

      const res = await fetch(url, {
        headers: {
          'X-User-Email': email,
          'X-User-Role': 'admin',
        },
      });

      if (res.ok) {
        const data = await res.json();
        setBookings(data.bookings || []);
      }
    } catch (error) {
      console.error('Error fetching queue:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    const email = localStorage.getItem('adminEmail') || prompt('Admin Email:');
    if (email) {
      setAdminEmail(email);
      fetchQueue(email);
    }
  }, [fetchQueue]);

  useEffect(() => {
    if (adminEmail) fetchQueue(adminEmail);
  }, [statusFilter, adminEmail, fetchQueue]);

  const updateStatus = async (bookingId, newStatus) => {
    try {
      const res = await fetch(`/api/admin/bookings/${bookingId}/status/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': adminEmail,
          'X-User-Role': 'admin',
        },
        body: JSON.stringify({
          status: newStatus,
          note: actionNote,
        }),
      });

      if (res.ok) {
        alert(`Booking updated to ${newStatus}`);
        setSelectedBooking(null);
        setActionNote('');
        fetchQueue(adminEmail);
      } else {
        alert('Error updating booking');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error updating booking');
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const totalPages = Math.max(1, Math.ceil(bookings.length / PAGE_SIZE));
  const visibleBookings = bookings.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const { showSkeleton, goToPage } = usePagedListUi({
    currentPage,
    setCurrentPage,
    loading,
    scrollTargetRef: listTopRef,
  });

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Admin Booking Queue</h1>
        <p className="text-slate-300">Review and manage booking requests</p>
      </div>

      {/* Filter */}
      <div className="flex gap-3 flex-wrap">
        {[
          { value: 'pending_review', label: 'Pending Review' },
          { value: 'approved', label: 'Approved' },
          { value: 'declined', label: 'Declined' },
        ].map((filter) => (
          <button
            key={filter.value}
            onClick={() => setStatusFilter(filter.value)}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              statusFilter === filter.value
                ? 'bg-brand-maroon text-white'
                : 'bg-white/5 text-slate-200 hover:bg-white/10 border border-white/10'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Queue */}
      {bookings.length === 0 ? (
        <div className="panel text-center py-12">
          {loading || showSkeleton ? (
            <p className="text-slate-300">Loading...</p>
          ) : (
            <>
              <FileText className="mx-auto mb-4 text-slate-400" size={48} />
              <p className="text-slate-300">No {statusFilter} bookings</p>
            </>
          )}
        </div>
      ) : (
        <div ref={listTopRef} className="space-y-4">
          <BusyTable show={showSkeleton} tone="public">
          {visibleBookings.map((booking) => {
            const colors = statusColors[booking.status] || statusColors.pending_review;

            return (
              <div key={booking.id} className={`panel ${colors.bg} cursor-pointer hover:shadow-md transition`}>
                <div
                  onClick={() => setSelectedBooking(selectedBooking?.id === booking.id ? null : booking)}
                  className="flex items-start justify-between gap-4"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-bold text-lg text-white">{booking.request_code}</h3>
                      <span className={`status-badge ${colors.badge}`}>
                        {booking.status.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-slate-200 font-semibold mb-3">{booking.event_name}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <label className="text-xs font-semibold text-slate-400 uppercase">Requester</label>
                        <p className="text-slate-200">{booking.requester_name}</p>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-400 uppercase">Type</label>
                        <p className="text-slate-200 capitalize">{booking.booking_type}</p>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-400 uppercase">Email</label>
                        <p className="text-slate-200 truncate">{booking.requester_email}</p>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-400 uppercase">Submitted</label>
                        <p className="text-slate-200">{new Date(booking.created_at).toLocaleDateString('en-PH', { timeZone: 'Asia/Manila' })}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Panel */}
                {selectedBooking?.id === booking.id && booking.status === 'pending_review' && (
                  <div className="mt-6 pt-6 border-t border-white/10 space-y-4">
                    <div>
                      <label>Admin Note (optional)</label>
                      <textarea
                        value={actionNote}
                        onChange={(e) => setActionNote(e.target.value)}
                        rows="3"
                        placeholder="Add notes about this booking..."
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => updateStatus(booking.id, 'approved')}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 transition"
                      >
                        <CheckCircle size={18} /> Approve
                      </button>
                      <button
                        onClick={() => updateStatus(booking.id, 'declined')}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 transition"
                      >
                        <XCircle size={18} /> Decline
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          </BusyTable>
          <BookingPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
          />
        </div>
      )}
    </div>
  );
}
